import { Box, Checkbox, Grid, TextField } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { MProfile } from '../../redux/scholarship.models';
import { selectLinkedChildProfile, selectProfilesChild } from '../../redux/scholarship.selectors';

interface CaptureSignatureProps {
  signedAgreementWording: string;
  parentLabel?: string;
  childLabel?: string;
  initialFirstName: string;
  initialLastName: string;
  initialChildFirstName: string;
  initialChildLastName: string;
  initialChecked?: boolean;
  initialSignatureText?: string;
  initialSignatureType?: string;
  initialSignatureFormat?: string;
  disableNames?: boolean;
  disableAll?: boolean;
  changeCallback: (data: {
    firstName: string;
    lastName: string;
    checked: boolean;
    signed: boolean;
    signedAgreementWording: string;
    signatureType?: string;
    signatureFormat?: string;
  }) => void;
}

export const CaptureSignature = ({
  signedAgreementWording,
  parentLabel,
  childLabel,
  initialFirstName,
  initialLastName,
  initialChildFirstName,
  initialChildLastName,
  initialChecked,
  initialSignatureText = 'By checking this box, I certify, under penalties of perjury, that I have agreed to submit this application' +
    ' electronically and that I have read, understand and agree to each of the terms above.',
  initialSignatureType,
  initialSignatureFormat,
  disableNames = false,
  disableAll = false,
  changeCallback
}: CaptureSignatureProps): JSX.Element => {
  const [firstName, setFirstName] = useState<string>(initialFirstName);
  const [lastName, setLastName] = useState<string>(initialLastName);
  const [childFirstName, setChildFirstName] = useState<string>(initialChildFirstName);
  const [childLastName, setChildLastName] = useState<string>(initialChildLastName);
  const [checked, setChecked] = useState<boolean>(!!initialChecked);
  const profilesChild = useSelector(selectProfilesChild);
  const linkedChildProfile = useSelector(selectLinkedChildProfile);

  if (!parentLabel) {
    parentLabel = 'Type Full Name of Signer';
  }

  useEffect(() => {
    changeCallback({
      firstName,
      lastName,
      checked,
      signed: checked,
      signedAgreementWording: signedAgreementWording,
      signatureType: initialSignatureType,
      signatureFormat: initialSignatureFormat
    });
  }, [firstName, lastName, checked, initialSignatureType, initialSignatureFormat, initialChecked]);

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked);
  };

  const useStyles = makeStyles(() =>
    createStyles({
      root: {
        backgroundColor: '#f3f6fd'
      },
      label: {
        fontSize: '0.9rem',
        fontWeight: 'bold'
      }
    })
  );

  useEffect(() => {
    const getChildName = () => {
      if (linkedChildProfile && Array.isArray(profilesChild)) {
        const thisProfile = profilesChild.find((item: MProfile) => item.id === linkedChildProfile);
        setChildFirstName(thisProfile?.firstName ?? '');
        setChildLastName(thisProfile?.lastName ?? '');
      }
    };
    getChildName();
  }, [linkedChildProfile, profilesChild]);

  const classes = useStyles();

  return (
    <div>
      <Box pt={3} pb={3} pl={3}>
        <Grid container spacing={3} className={classes.root}>
          <Grid item xs={12}>
            <Typography className={classes.label}>{parentLabel}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  id="firstName"
                  name="firstName"
                  label="Parent First Name"
                  value={firstName}
                  disabled={disableNames || disableAll}
                  fullWidth
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  id="lastName"
                  name="lastName"
                  label="Parent Last Name"
                  value={lastName}
                  disabled={disableNames || disableAll}
                  fullWidth
                  onChange={(event) => setLastName(event.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.label}>{childLabel}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="firstNameChild"
                  name="firstNameChild"
                  label="Child First Name"
                  value={childFirstName}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="lastNameChild"
                  name="lastNameChild"
                  label="Child Last Name"
                  value={childLastName}
                  disabled={true}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              value={'on'}
              disabled={disableAll}
              control={<Checkbox name={'signature'} checked={checked} onChange={handleCheckbox} />}
              label={<Typography className={classes.label}>{initialSignatureText}</Typography>}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};
